import { Avatar, AvatarShape, ButtonIcon, ButtonSize, ButtonVariant, IconVariants, Image, Modal, ModalSize } from "@next-platform/client-common-spicy-ui";
import { ChatRole } from "@next-platform/client-common-core";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "@/constants";
import { AvatarPreset } from "@/types";
import { getAvatarUrl } from "@/utils";

import { AvatarWithModalProps } from "./types";

const AvatarWithModal = ({
  id,
  name,
  src = "",
  blurred,
  role = ChatRole.BOT,
  children,
  isOpen,
  onOpenChange,
  size,
  classNames,
  isClickable = true,
}: AvatarWithModalProps) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const isControlled = isOpen !== undefined;
  const isModalOpen = isControlled ? isOpen : showModal;

  const handleOpenChange = (value: boolean) => {
    if (isControlled) {
      onOpenChange?.(value);
    } else {
      setShowModal(value);
    }
  };

  const handleAvatarClick = () => {
    if (src) {
      return handleOpenChange(true);
    }
    if (role === ChatRole.USER) {
      return navigate(ROUTES.profile);
    }
    if (role === ChatRole.BOT && id) {
      return navigate(`${ROUTES.chatbot}/${id}`);
    }
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.code === "Escape") {
        handleOpenChange(false);
      }
    };
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isModalOpen]);

  return (
    <>
      {children || (
        <Avatar
          src={getAvatarUrl(src, { preset: AvatarPreset.AVATAR_48X48 }) as string}
          name={name}
          blurred={blurred}
          onClick={isClickable ? handleAvatarClick : undefined}
          shape={AvatarShape.SQUARE}
          size={size}
          classNames={classNames}
        />
      )}
      {isModalOpen && src && (
        <Modal
          hideCloseButton
          open={true}
          backdrop="blur"
          size={ModalSize.Free}
          className="max-h-full max-w-full size-full !bg-transparent dark:!bg-transparent"
        >
          {createPortal(
            <ButtonIcon
              icon={IconVariants.CLOSE}
              variant={ButtonVariant.TRANSPARENT}
              size={ButtonSize.LARGE}
              className="fixed top-6 right-lg z-[99999999999]"
              onClick={() => handleOpenChange(false)}
            />,
            document.body,
          )}
          <div className="size-full flex justify-center items-center p-lg" onClick={() => handleOpenChange(false)}>
            <div className="size-full flex justify-center items-center max-h-[832px]">
              <Image
                onClick={(e) => e.stopPropagation()}
                className={`max-w-full max-h-full rounded-xl object-contain ${blurred ? "blur-md" : ""}`}
                src={getAvatarUrl(src, { returnWithOriginalRatio: true }) as string}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default AvatarWithModal;
